import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { useShowcaseContext } from '../contexts/ShowcaseContext'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_firestore, firebase_functions } from '../../../common/firebaseConfig'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import usePaginatedLoader from '../../../hooks/usePaginatedLoader'
import ReportCardList from '../../../components/backtests/ReportCardList'
import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import { useCollectionData } from 'react-firebase-hooks/firestore'
import { orderBy, query } from 'firebase/firestore'
import { collection, where, limit } from 'firebase/firestore'
import { useHiddenBacktests } from '../../../hooks/useHiddenBacktests'
import FolderRail from '../../../components/backtests/folders/FolderRail'

function ShowcaseBacktestReports() {
  const { tradingHouse, usermeta } = useApplicationSuiteContext()

  const {
    backtestFilters,
    backtestSort,
    type,
    focusedBacktest,
    setFocusedBacktest,
    folders,
    foldersLoading,
    createFolder,
    renameFolder,
    deleteFolder,
  } = useShowcaseContext()

  const [lastQueryTime, setLastQueryTime] = useState(new Date())
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const hiddenBacktestIds = useHiddenBacktests(usermeta?.gcid)

  // A selected folder can go stale if it's deleted from another session/device
  // while this view is open — reconcile against the live folder list once it
  // loads so there's a way back to a valid state. Gated on type === 'favorites'
  // (the subscription is disabled otherwise, so an empty `folders` there means
  // "not watching," not "deleted" — switching type away and back intentionally
  // keeps the last-viewed folder selected) and !foldersLoading (so this doesn't
  // fire against the empty array during the brief window before the listener's
  // first snapshot arrives).
  useEffect(() => {
    if (
      type === 'favorites' &&
      !foldersLoading &&
      selectedFolderId &&
      !folders.some((folder) => folder.id === selectedFolderId)
    ) {
      setSelectedFolderId(null)
    }
  }, [type, foldersLoading, folders, selectedFolderId])

  const [getShowcaseBacktests] = useHttpsCallable(firebase_functions, 'getBacktests')

  const func = useCallback(
    (params: any) =>
      getShowcaseBacktests({
        ...params,
        tradingHouse,
        backtestFilters,
        sort: backtestSort,
        type,
        folderId: type === 'favorites' ? (selectedFolderId ?? undefined) : undefined,
      }),
    [getShowcaseBacktests, tradingHouse, backtestFilters, backtestSort, type, selectedFolderId]
  )

  const {
    data: backtests,
    loadMore,
    isLoading,
    totalCount,
    reset,
  } = usePaginatedLoader({
    queryFunction: func,
    take: 50,
  })

  // The favorites list is folder-scoped server-side (via folderId), so a
  // backtest moving in/out of the currently viewed folder — from anywhere:
  // the settings panel, another tab, another device — won't be reflected
  // until the paginated query re-runs from scratch. Watches the selected
  // folder's own backtestIds rather than hooking into one specific move
  // call site, so it covers every source of a folder-membership change.
  // Skips the case where `selectedFolderId` itself just changed — that's
  // already a fresh fetch via `func`'s own dependency on it.
  const selectedFolderSnapshotRef = useRef<{ folderId: string | null; backtestIds: string[] }>({
    folderId: null,
    backtestIds: [],
  })
  useEffect(() => {
    const selectedFolder = folders.find((folder) => folder.id === selectedFolderId)
    const currentBacktestIds = selectedFolder?.backtestIds ?? []
    const previous = selectedFolderSnapshotRef.current

    if (
      selectedFolderId !== null &&
      previous.folderId === selectedFolderId &&
      JSON.stringify(previous.backtestIds) !== JSON.stringify(currentBacktestIds)
    ) {
      reset()
    }

    selectedFolderSnapshotRef.current = {
      folderId: selectedFolderId,
      backtestIds: currentBacktestIds,
    }
  }, [folders, selectedFolderId, reset])

  const [runningBacktests] = useCollectionData(
    query(
      collection(firebase_firestore, `usermeta/${usermeta.gcid}/backtests`),
      where('backtestSettings.tradingHouse', '==', tradingHouse),
      where('status', 'in', ['running']),
      orderBy('startTime', 'desc')
    )
  )

  // Query for backtests that completed since our last query
  const [recentlyCompletedBacktests] = useCollectionData(
    query(
      collection(firebase_firestore, `usermeta/${usermeta.gcid}/backtests`),
      where('backtestSettings.tradingHouse', '==', tradingHouse),
      where('status', 'in', ['complete', 'error']),
      where('endTime', '>=', lastQueryTime.getTime()),
      limit(1)
    )
  )

  useEffect(() => {
    if (recentlyCompletedBacktests && recentlyCompletedBacktests.length > 0 && type === 'user') {
      setLastQueryTime(new Date())
      reset()
    }
  }, [recentlyCompletedBacktests, reset, type])

  const getBacktestTypeLabel = useCallback(() => {
    switch (type) {
      case 'showcase':
        return 'Showcase'
      case 'favorites':
        return 'Favorite'
      case 'user':
        return 'Your'
      case 'featured':
        return 'Basic'
      default:
        return ''
    }
  }, [type])

  const headerComponent = useMemo(() => {
    return (
      <>
        {runningBacktests && runningBacktests.length > 0 && (
          <>
            <Typography
              variant="overline"
              fontWeight="700"
              color="text.secondary"
              sx={{ letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block' }}
              py={2}
              px={1}
            >
              Running Backtests ({runningBacktests.length}/5)
            </Typography>
            <Box height={150}>
              <ReportCardList
                backtests={runningBacktests}
                condensed
                hiddenBacktestIds={hiddenBacktestIds}
              />
            </Box>
          </>
        )}
        {!!runningBacktests?.length && (
          <Typography
            variant="overline"
            fontWeight="700"
            color="text.secondary"
            sx={{ letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block' }}
            py={2}
            px={1}
          >
            {getBacktestTypeLabel()} Backtests
          </Typography>
        )}
      </>
    )
  }, [getBacktestTypeLabel, runningBacktests, hiddenBacktestIds])

  return (
    <>
      {type === 'favorites' && (
        <FolderRail
          folders={folders}
          selectedFolderId={selectedFolderId}
          onSelectFolder={setSelectedFolderId}
          onCreateFolder={createFolder}
          onRenameFolder={renameFolder}
          onDeleteFolder={deleteFolder}
        />
      )}
      <ReportCardList
        backtests={backtests}
        loadMore={loadMore}
        isLoading={isLoading}
        totalCount={totalCount}
        headerComponent={headerComponent}
        emptyListText={{
          title: 'No backtests found',
          subTitle: 'Try adjusting your filters',
        }}
        activeBacktest={focusedBacktest}
        onBacktestSelection={setFocusedBacktest}
        hiddenBacktestIds={hiddenBacktestIds}
      />
    </>
  )
}

export default ShowcaseBacktestReports
